import { forwardRef, Suspense, useRef } from "react"
import { FormikProps } from "formik"
import { enqueueSnackbar } from "notistack"
import { useNavigate, useParams } from "react-router-dom"
import { MERCHANT_ADMIN_PAYMENT_LINKS_ROUTE, MERCHANT_ADMIN_ROUTE } from "../../../../../constants/routes"
import { QueryErrorBoundary } from "../../../../../core/api/components/QueryErrorBoundary"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { LoadingSpinner } from "../../../../../shared/components/loading/LoadingSpinner"
import { CreateUpdatePaymentLinkForm } from "../components/CreateUpdatePaymentLinkForm"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { queryKeyFor, useApiQuery } from "../../../../../core/api/useApiQuery"
import { CreateUpdatePaymentLinkFormValues } from "../paymentLinksTypes"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { prepareInitialValues } from "../helpers/prepareInitialValues"
import { useDateTime } from "../../../../localization/useDateTime"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import {
    paymentLinkDetails,
    PaymentLinkDetailsResponse,
    PaymentLinksQuery,
    updatePaymentLink,
} from "../paymentLinksApi"

function transformData(data: PaymentLinkDetailsResponse) {
    return data.paymentLink
}

export function EditPaymentLink() {
    const formRef = useRef<FormikProps<CreateUpdatePaymentLinkFormValues>>(null)

    const handleSaveClick = () => {
        formRef.current?.submitForm()
    }

    return (
        <>
            <LocalizedStrict id="payment-links-edit-page-header" attrs={{ title: true }}>
                <PageHeader
                    title="Edit payment link"
                    page={Page.MERCHANT_PAYMENT_LINK_EDIT}
                    rightElement={
                        <LocalizedStrict id="payment-links-form-edit-submit-button">
                            <StandardButton onClick={handleSaveClick}>Save</StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-shops">
                            <Breadcrumb>Shops</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-payment-links">
                            <BreadcrumbLink to={MERCHANT_ADMIN_PAYMENT_LINKS_ROUTE}>Payment links</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="payment-links-edit-page">
                            <Breadcrumb>Edit payment link</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <PaymentLink ref={formRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}

const PaymentLink = forwardRef<FormikProps<CreateUpdatePaymentLinkFormValues>>((_, ref) => {
    const { paymentLinksId } = useParams()
    const navigate = useNavigate()

    const dateTimeFactory = useDateTime()

    const { data: paymentLink } = useApiQuery(paymentLinkDetails, {
        queryName: PaymentLinksQuery.DETAILS,
        dependencies: {
            paymentLinksId: paymentLinksId || "",
        },
        suspense: true,
        useErrorBoundary: true,
        enabled: Boolean(paymentLinksId),
        select: transformData,
    })

    const { mutate: callUpdatePaymentLink } = useApiMutation(updatePaymentLink, {
        async onSuccess(params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [PaymentLinksQuery.LIST] })
            await context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(PaymentLinksQuery.DETAILS, { departmentId: params.paymentLinksId }),
            })
            navigate(MERCHANT_ADMIN_PAYMENT_LINKS_ROUTE)

            if (!data.serviceResponse.displayMessage) return
            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
        },
    })

    return (
        <CreateUpdatePaymentLinkForm
            ref={ref}
            initialValues={prepareInitialValues(dateTimeFactory, paymentLink)}
            onSubmit={(data) => {
                paymentLinksId &&
                    callUpdatePaymentLink({
                        paymentLinksId,
                        paymentLink: data,
                    })
            }}
        />
    )
})
